import get from 'lodash.get';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';
import { cleanArray } from './Array-functions';
import type { StringIndexedObject } from '../types';

/**
 * Checks if the input is an empty object
 *
 * @param {Any} input
 */
export const isEmptyObject = (input) => {
  if (!(input instanceof Object)) return false;
  if (input instanceof Array) return false;
  return Object.keys(input).length === 0;
};

/**
 * Checks if the input is an empty array
 *
 * @param {Any} input
 */
export const isEmptyArray = (input) => {
  if (!(input instanceof Object)) return false;
  if (!(input instanceof Array)) return false;
  return input.length === 0;
};

/**
 * Cleans the JS object.
 *
 * Any field with empty string or null values are removed from the object. This
 * was developed for cleaning the API request payload.
 *
 * @param {Object} inputObject
 */
export const cleanObject = (object) =>
  Object.keys(object).reduce((cleaned, key) => {
    if (
      object[key] &&
      typeof object[key] === 'object' &&
      !Array.isArray(object[key]) &&
      !(object[key] instanceof Date)
    ) {
      const newObject = cleanObject(object[key]);
      if (isEmptyObject(newObject)) return cleaned;
      return {
        ...cleaned,
        [key]: newObject,
      };
    }
    if (
      object[key] &&
      typeof object[key] === 'object' &&
      Array.isArray(object[key])
    ) {
      const newArray = cleanArray(object[key]);
      if (isEmptyArray(newArray)) return cleaned;
      return {
        ...cleaned,
        [key]: newArray,
      };
    }
    if (
      !(object[key] instanceof Date) &&
      (object[key] === '' ||
        object[key] === null ||
        object[key] === undefined ||
        isEmptyObject(object[key]) ||
        isEmptyArray(object[key]) ||
        Number.isNaN(object[key]))
    ) {
      return cleaned;
    }

    return {
      ...cleaned,
      [key]: object[key],
    };
  }, {});

export const getKeys = (
  object: StringIndexedObject,
  currentPath = '',
  dotNotation = true,
): string[] =>
  Object.keys(object).reduce<string[]>((all: string[], path: string) => {
    const value = object[path];
    // If undefined or null value, return paths
    if (value === undefined || value === null) return all;
    // Add path to end of current path including `.`
    const concatPath = `${currentPath ? `${currentPath}.` : ''}${path}`;
    // Handle array
    if (Array.isArray(value)) {
      const paths = value.reduce<string[]>(
        (nested: string[], current: StringIndexedObject, index: number) => {
          const currentArrayPath = dotNotation
            ? `${concatPath}.${index}`
            : `${concatPath}[${index}]`;
          const arrayPath = getKeys(current, currentArrayPath, dotNotation);
          nested = nested.concat(arrayPath);
          return nested;
        },
        [],
      );
      all = all.concat(paths);
      return all;
    }
    // Handle object
    if (typeof value === 'object') {
      const nestedKeys = getKeys(value, concatPath, dotNotation);
      all = all.concat(nestedKeys);
      return all;
    }
    all.push(concatPath);
    return all;
  }, []);

export const replacePasswordValues = (
  object: StringIndexedObject,
): StringIndexedObject => {
  const newObject = cleanObject(cloneDeep(object));
  const passwordPaths = getKeys(newObject).reduce((paths, path) => {
    if (path.includes('password')) paths.push(path);
    return paths;
  }, []);
  passwordPaths.forEach((path) => {
    try {
      const value = get(newObject, path);
      if (typeof value !== 'string') return;
      const str = value.replace(/./g, '*');
      set(newObject, path, str);
      return;
    } catch (e) {
      return;
    }
  });
  return newObject;
};

export const replaceKeyWordValues = (
  object: StringIndexedObject,
  keywords: string[] = ['content'],
): StringIndexedObject => {
  const newObject = cleanObject(cloneDeep(object));
  const keywordPaths = getKeys(newObject).reduce((paths, path) => {
    if (keywords.some((key) => path.includes(key))) paths.push(path);
    return paths;
  }, []);
  keywordPaths.forEach((path) => {
    try {
      const value = get(newObject, path);
      if (typeof value !== 'string') return;
      const str = `${value.slice(0, 10)}*`;
      set(newObject, path, str);
      return;
    } catch (e) {
      set(newObject, path, '*');
      return;
    }
  });
  return newObject;
};
